export type GrantAffinityEntityType =
  | "city"
  | "county"
  | "state"
  | "special_district"
  | "nonprofit_partner";

export type GrantAffinityProjectSize =
  | "unknown"
  | "under_100k"
  | "from_100k_to_1m"
  | "over_1m";

export type MatchmakerFollowUpQuestion = {
  id: string;
  prompt: string;
  input_type: "boolean" | "select" | "text";
  options?: Array<{ value: string; label: string }>;
};

export type GrantAffinityProfile = {
  id: number;
  municipality_id: number;
  organization_name: string;
  state: string | null;
  entity_type: GrantAffinityEntityType;
  priority_areas: string[];
  funding_priorities: string | null;
  typical_project_size: GrantAffinityProjectSize;
  disqualifiers: string | null;
  matchmaker_answers?: Record<string, string>;
  follow_up_questions?: MatchmakerFollowUpQuestion[];
  complete: boolean;
  embedded: boolean;
  embed_failed?: boolean;
  completed_at: string | null;
  profile_embedded_at: string | null;
  updated_at: string;
  priority_area_options: string[];
};

export type GrantAffinityProfilePrefill = {
  organization_name: string;
  state: string | null;
  entity_type: GrantAffinityEntityType;
};

export type GrantAffinityProfileResponse =
  | GrantAffinityProfile
  | {
    profile: null;
    prefill: GrantAffinityProfilePrefill;
    priority_area_options: string[];
  };

export type GrantAffinityProfilePayload = {
  organization_name: string;
  state?: string | null;
  entity_type: GrantAffinityEntityType;
  priority_areas: string[];
  funding_priorities: string;
  typical_project_size?: GrantAffinityProjectSize;
  disqualifiers?: string | null;
  matchmaker_answers?: Record<string, string>;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

export function isGrantAffinityProfile(
  response: GrantAffinityProfileResponse,
): response is GrantAffinityProfile {
  return "id" in response && typeof response.id === "number";
}

export async function getGrantAffinityProfile(
  municipalityId: number,
): Promise<GrantAffinityProfileResponse> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/grant_affinity_profile`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load grant affinity profile");
  }
  return response.json();
}

export async function upsertGrantAffinityProfile(
  municipalityId: number,
  payload: GrantAffinityProfilePayload,
): Promise<GrantAffinityProfile> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/grant_affinity_profile`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_affinity_profile: payload }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = Array.isArray(data.errors) ? data.errors.join(", ") : "Failed to save profile";
    throw new Error(message);
  }
  if (data.embed_failed) {
    throw new Error(
      "Profile saved, but recommendations could not be prepared. Please try saving again in a moment.",
    );
  }
  return data;
}
